import React, { useState, useEffect } from 'react';
import { ConversationService, ConversationSummary } from '../services/conversationService';
import { chatApi, ChatSummaryItem } from '../services/chatApi';
import LoadingScreen from './ui/LoadingScreen';

interface ConversationsPageProps {
  onSelectConversation: (conversationId: string) => void;
  onNewChat: () => void;
}

const ConversationsPage: React.FC<ConversationsPageProps> = ({ onSelectConversation, onNewChat }) => {
  const [conversations, setConversations] = useState<(ConversationSummary | ChatSummaryItem)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const res = await chatApi.getChatsSummary();
      setConversations(res.chats);
      setError(null);
    } catch (err) {
      console.warn('Chats summary not available:', err);
      setConversations([]);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this conversation?')) {
      return;
    }

    try {
      const item = conversations.find(c => ((c as any).id || (c as any).chat_id) === conversationId);
      const companionId = (item as any)?.companion_id;
      if (companionId) {
        await chatApi.deleteChatByCompanion(companionId);
      } else {
        await ConversationService.deleteConversation(conversationId);
      }
      setConversations(prev => prev.filter(conv => (conv as any).id !== conversationId && (conv as any).chat_id !== conversationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingScreen
          title="Loading Conversations"
          message="Fetching your chat history..."
        />
      </div>
    );
  }

  return (
    <div className="glass-effect rounded-2xl p-4 sm:p-8 border border-dark-600 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent-purple/20 to-transparent rounded-full blur-2xl"></div>
      <div className="relative">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-white">
            💬 Your Conversations
          </h2>
          <button
            onClick={onNewChat}
            className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300 text-center"
          >
            ✨ New Chat
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-400 text-sm text-center mb-6">
            {error}
          </div>
        )}

        {conversations.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="text-4xl sm:text-6xl mb-4">💬</div>
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">No conversations yet</h3>
            <p className="text-dark-300 mb-6 text-sm sm:text-base">Start your first conversation with an AI companion!</p>
            <button
              onClick={onNewChat}
              className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
            >
              Start Chatting
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {conversations.map((conversation) => (
              <div
                key={(conversation as any).id || (conversation as any).chat_id}
                onClick={() => onSelectConversation((conversation as any).id || (conversation as any).chat_id)}
                className="glass-effect border border-dark-600 rounded-xl p-4 sm:p-6 cursor-pointer hover:border-accent-purple/50 transition-all duration-300 group"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-2 group-hover:text-accent-purple transition-colors truncate">
                      {(conversation as any).title || (conversation as any).companion_name || `Chat with ${(conversation as any).chatbot_name || 'Companion'}`}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm text-dark-300">
                      <span className="inline-flex items-center gap-1">
                        🤖 {(conversation as any).chatbot_name || (conversation as any).companion_name || 'Companion'}
                      </span>
                      <span className="hidden sm:inline">•</span>
                      <span>{formatDate((conversation as any).updated_at || (conversation as any).latest_message_timestamp || (conversation as any).created_at)}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteConversation(((conversation as any).id || (conversation as any).chat_id), e)}
                    className="self-end sm:self-auto px-2 sm:px-3 py-1 sm:py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-300 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">🗑️ Delete</span>
                    <span className="sm:hidden">🗑️</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;